import useDateFormatters from "@hooks/useDateFormatters"

import { faIcon } from "@utils/faIcon"

import { Avatar } from "@atoms"

import {
    IssueDateBadge,
    LabelsBadges,
    LastModifiedBadge,
    TotalBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { VendorCreditCardProps } from "./VendorCreditCard.types"

const vendorIcon = faIcon({ name: "store", style: "solid" })

type VendorCreditTimestampKey = "created_at" | "last_recorded_at" | "last_consumed_at" | "last_voided_at"

const STATUS_TRANSITION: Record<
    VendorCredit["status_label"],
    { verb: string; timestampKey: VendorCreditTimestampKey }
> = {
    Draft: { verb: "Created", timestampKey: "created_at" },
    Recorded: { verb: "Recorded", timestampKey: "last_recorded_at" },
    Consumed: { verb: "Consumed", timestampKey: "last_consumed_at" },
    Voided: { verb: "Voided", timestampKey: "last_voided_at" },
}

export default function VendorCreditCard(props: VendorCreditCardProps) {
    const {
        vendorCredit,
        displayedAttributes,
        onClick,
        isLoading = false,
        isTabbable,
        className,
        ...errorProps
    } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    // A non-draft pending credit, which only the kitchen admin can write, gets the raw "Pending" label from the API
    // (the status badge falls back on it too), so an unmapped label takes the draft tooltip instead of throwing.
    const { verb, timestampKey } =
        (STATUS_TRANSITION as Record<string, (typeof STATUS_TRANSITION)[keyof typeof STATUS_TRANSITION]>)[
            vendorCredit.status_label
        ] ?? STATUS_TRANSITION.Draft
    const statusTooltip = formatDatetimeTooltip(verb, vendorCredit[timestampKey] ?? vendorCredit.created_at)

    // Every credit carries its amounts and both dates, so only the labels can leave a badge with nothing to show.
    const showTotal = shouldShowAttribute(displayedAttributes, "subtotal")
    const showRemaining = shouldShowAttribute(displayedAttributes, "amount_remaining")
    const showIssued = shouldShowAttribute(displayedAttributes, "date_issued")
    const showLastModified = shouldShowAttribute(displayedAttributes, "last_modified_at")
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && vendorCredit.labels.length > 0

    const hasAttributes = showTotal || showRemaining || showIssued || showLastModified || showLabels

    return (
        <ObjectCard
            onClick={onClick}
            isLoading={isLoading}
            isTabbable={isTabbable}
            className={className}
            {...errorProps}
        >
            <ObjectCard.Header
                id={vendorCredit.custom_id ?? String(vendorCredit.id)}
                subId={vendorCredit.bill_id ?? undefined}
                subIdLabel="Bill ID"
                statusBadge={
                    <ObjectStatusBadge
                        objectType="vendor_credit"
                        status={vendorCredit.status_label}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body
                avatar={<Avatar variant="object" icon={vendorIcon} size="xl" />}
                primaryText={vendorCredit.vendor_name}
                secondaryText={vendorCredit.billing_address_formatted ?? undefined}
                secondaryTextPlaceholder="No billing address"
                secondaryTextTooltip={vendorCredit.billing_address_formatted_multiline ?? undefined}
            />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showTotal && <TotalBadge value={vendorCredit.subtotal} />}
                    {showRemaining && <TotalBadge value={vendorCredit.amount_remaining} label="Remaining" />}
                    {showIssued && <IssueDateBadge date={vendorCredit.date_issued} />}
                    {showLastModified && <LastModifiedBadge date={vendorCredit.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={vendorCredit.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
